"use client";

import { useEffect, useRef, Suspense, useState, useCallback } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { RegistrationForm } from "@/components/registration-form";
import { HuntView } from "@/components/hunt-view";
import { OnboardingScreen } from "@/components/onboarding-screen";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import Image from "next/image";
import { track } from "@vercel/analytics";
import { useOrientation } from "@/components/ui/use-orientation";
import { Trophy, WifiOff, RefreshCw } from "lucide-react";
import { useSession } from "@/hooks/use-session";
import { useHunt } from "@/hooks/use-hunt";
import { useEvent } from "@/hooks/use-event";
import { EventWelcomeBanner } from "@/components/event-welcome-banner";

function LoadingScreen() {
  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-[hsl(217,50%,21%)] to-[hsl(223,54%,31%)]">
      <div className="flex flex-col items-center gap-4">
        <div className="flex items-center gap-3">
          <Image
            src="https://assets.fluidityiq.ai/assets/fiq-logo-icon-small.png"
            alt="FluidityIQ"
            width={48}
            height={48}
            className="h-12 w-12"
            priority
          />
          <Image
            src="https://assets.fluidityiq.ai/assets/fiq-logo.png"
            alt="FluidityIQ"
            width={140}
            height={40}
            className="-mb-[10px] -ml-[10px] h-10"
            style={{ width: 'auto' }}
            priority
          />
        </div>
        <div className="h-1 w-full overflow-hidden rounded-full bg-white/20">
          <div className="h-full w-1/3 animate-[indeterminate_1.5s_ease-in-out_infinite] rounded-full bg-white" />
        </div>
      </div>
    </div>
  );
}

function ErrorScreen({ onRetry, isRetrying }: { onRetry: () => void; isRetrying: boolean }) {
  return (
    <div className="fixed inset-0 flex flex-col bg-gradient-to-b from-[hsl(217,50%,21%)] to-[hsl(223,54%,31%)]">
      <header className="pt-safe">
        <div className="flex items-center gap-3 px-4 py-3">
          <Image
            src="https://assets.fluidityiq.ai/assets/fiq-logo-icon-small.png"
            alt="FluidityIQ"
            width={40}
            height={40}
            className="h-10 w-10"
          />
          <Image
            src="https://assets.fluidityiq.ai/assets/fiq-logo.png"
            alt="FluidityIQ"
            width={120}
            height={32}
            className="-mb-[10px] -ml-[10px] h-8"
            style={{ width: 'auto' }}
          />
        </div>
      </header>

      <div className="flex flex-1 items-center justify-center px-6">
        <Card className="w-full max-w-sm rounded-2xl">
          <CardContent className="flex flex-col items-center gap-5 pt-8 pb-8 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
              <WifiOff className="h-7 w-7 text-muted-foreground" />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-semibold">Something went wrong</h2>
              <p className="text-sm leading-relaxed text-muted-foreground">
                We&apos;re having trouble connecting right now. Your progress is safe — tap below to try again.
              </p>
            </div>
            <Button
              onClick={onRetry}
              disabled={isRetrying}
              size="lg"
              className="w-full rounded-xl"
            >
              {isRetrying ? (
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              {isRetrying ? "Retrying…" : "Try Again"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function HomeContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { session, isLoading: sessionLoading, error: sessionError, mutate: mutateSession } = useSession();
  const { hunt, items, completions, isLoading: huntLoading, mutate: mutateHunt } = useHunt();
  const { event } = useEvent();
  const [isRetrying, setIsRetrying] = useState(false);
  const { isLandscape } = useOrientation();
  const prevLandscapeRef = useRef(isLandscape);
  const hasEverLoadedRef = useRef(false);

  // useHunt includes session loading so we do not drop the global loader before hunt fetch starts.
  const isLoading = huntLoading;
  const hasError = !!sessionError && !session;

  if (!isLoading) hasEverLoadedRef.current = true;

  const completedCount = completions.length;
  const totalCount = items.length;
  const dayNumber = hunt?.day_number;

  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    try {
      await Promise.all([mutateSession(), mutateHunt()]);
    } finally {
      setIsRetrying(false);
    }
  }, [mutateSession, mutateHunt]);

  useEffect(() => {
    if (isLandscape && !prevLandscapeRef.current) {
      track("device_rotated_landscape");
    }
    prevLandscapeRef.current = isLandscape;
  }, [isLandscape]);

  useEffect(() => {
    if (session && searchParams.get("referrer")) {
      const newParams = new URLSearchParams(searchParams.toString());
      newParams.delete("referrer");
      const newUrl = newParams.toString() ? `?${newParams.toString()}` : window.location.pathname;
      router.replace(newUrl, { scroll: false });
    }
  }, [session, searchParams, router]);

  const handleRegistrationSuccess = () => {
    window.scrollTo(0, 0);
    mutateSession();
    mutateHunt();
  };

  const handleOnboardingAccept = () => {
    window.scrollTo(0, 0);
    mutateSession();
    mutateHunt();
  };

  if (isLoading && !hasEverLoadedRef.current && !isRetrying) {
    return <LoadingScreen />;
  }

  if (hasError || (isRetrying && !session)) {
    return <ErrorScreen onRetry={handleRetry} isRetrying={isRetrying} />;
  }

  if (!session) {
    return (
      <div className="flex min-h-[100dvh] flex-col bg-background">
        <header className="bg-[hsl(217,50%,21%)] pt-safe">
          <div className="flex items-center gap-3 px-4 py-3">
            <Image
              src="https://assets.fluidityiq.ai/assets/fiq-logo-icon-small.png"
              alt="FluidityIQ"
              width={40}
              height={40}
              className="h-10 w-10"
            />
            <Image
              src="https://assets.fluidityiq.ai/assets/fiq-logo.png"
              alt="FluidityIQ"
              width={120}
              height={32}
              className="-mb-[10px] -ml-[10px] h-8"
              style={{ width: 'auto' }}
            />
          </div>
        </header>

        <EventWelcomeBanner
          eventName={event?.name}
          welcomeMessage={event?.welcome_message}
          logoUrl={event?.logo_url}
        />

        <main className="flex-1 px-4 py-5 pb-safe landscape:px-safe-or-4">
          <Card className="rounded-2xl shadow-sm">
            <CardHeader className="pb-1">
              <CardTitle className="text-xl">Join the Hunt</CardTitle>
              <CardDescription className="text-base">
                Participate in today&apos;s patent scavenger hunt and win prizes!
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RegistrationForm onSuccess={handleRegistrationSuccess} />
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-[100dvh] flex-col">
      <header className="hunt-header bg-[hsl(217,50%,21%)] pt-safe landscape:px-safe-or-4">
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <div className="flex shrink-0 items-center gap-3">
            <Image
              src="https://assets.fluidityiq.ai/assets/fiq-logo-icon-small.png"
              alt="FluidityIQ"
              width={40}
              height={40}
              className="h-10 w-10"
            />
            <Image
              src="https://assets.fluidityiq.ai/assets/fiq-logo.png"
              alt="FluidityIQ"
              width={120}
              height={32}
              className="-mb-[10px] -ml-[10px] h-8 w-auto"
              style={{ width: "auto" }}
            />
          </div>
          {session && totalCount > 0 && dayNumber && (
            <div className="flex items-center gap-2">
                  {completedCount >= totalCount ? (
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-500 shadow-md">
                      <Trophy className="h-5 w-5 text-white" />
                    </div>
                  ) : (
                    <div
                      className="relative flex h-10 w-10 shrink-0 items-center justify-center"
                      role="progressbar"
                      aria-valuenow={completedCount}
                      aria-valuemin={0}
                      aria-valuemax={totalCount}
                      aria-label={`${completedCount} of ${totalCount} items completed`}
                    >
                      <svg className="h-10 w-10 -rotate-90" viewBox="0 0 40 40" aria-hidden="true">
                        <circle
                          cx="20"
                          cy="20"
                          r="16"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          className="text-white/30"
                        />
                        <circle
                          cx="20"
                          cy="20"
                          r="16"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeDasharray={2 * Math.PI * 16}
                          strokeDashoffset={2 * Math.PI * 16 * (1 - (completedCount / totalCount))}
                          className="text-white transition-all duration-500"
                        />
                      </svg>
                      <span className="absolute text-xs font-bold text-white" aria-hidden="true">
                        {completedCount}/{totalCount}
                      </span>
                    </div>
                  )}
                  <span className="shrink-0 whitespace-nowrap rounded-full bg-primary px-4 py-1.5 text-base font-bold text-primary-foreground">
                    Day {dayNumber}
                  </span>
            </div>
          )}
        </div>
      </header>

      <EventWelcomeBanner
        eventName={event?.name}
        welcomeMessage={event?.welcome_message}
        logoUrl={event?.logo_url}
        className="landscape:safe-x"
      />

      <main className="flex-1 px-4 py-4 pb-safe">
        {session && session.terms_accepted_at ? (
          <HuntView userName={session.name} userEmail={session.email} userCompany={session.company} />
        ) : session ? (
          <div className="flex flex-col gap-4">
            <Skeleton className="h-48 w-full rounded-2xl" />
            <Skeleton className="h-24 w-full rounded-2xl" />
            <Skeleton className="h-24 w-full rounded-2xl" />
          </div>
        ) : null}
      </main>

      {!isLoading && session && !session.terms_accepted_at && (
        <div className="fixed inset-0 z-50">
          <OnboardingScreen onAccept={handleOnboardingAccept} />
        </div>
      )}

    </div>
  );
}

export default function Home() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <HomeContent />
    </Suspense>
  );
}
